import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import ReportLoader from "../Reports/Loader";

// Import report sections
import Source from "./source";
import Services from "./services";
import Booking from "./booking";
import Conversion from "./conversion";
import Detailed from "./detailed";
import Employees from "./employees";
import Payments from "./payments";

// Color scheme
const colors = {
  bgPrimary: "#F8FAFC",
  primary: "#6366F1",
  primaryDark: "#4F46E5",
  textPrimary: "#1E293B",
  textSecondary: "#64748B",
  border: "#E2E8F0",
  white: "#FFFFFF",
};

// Storage keys for persisting state
const STORAGE_KEYS = {
  ACTIVE_TAB: "reports_v2_active_tab",
  DATE_RANGE: "reports_v2_date_range",
};

// Get default date range (current month)
const getDefaultDateRange = () => {
  const now = dayjs();
  return {
    startDate: now.startOf("month").toDate(),
    endDate: now.endOf("month").toDate(),
  };
};

// Tab configuration
const TABS = [
  { key: "source", label: "Sources" },
  { key: "services", label: "Services" },
  { key: "booking", label: "Bookings" },
  { key: "conversion", label: "Conversions" },
  { key: "detailed", label: "Detailed" },
  { key: "employees", label: "Employees" },
  { key: "payments", label: "Payments" },
];

const Reports_v2 = () => {
  // Initialize state from localStorage
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.ACTIVE_TAB);
    return saved || "source";
  });

  const [dateRange, setDateRange] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.DATE_RANGE);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        startDate: new Date(parsed.startDate),
        endDate: new Date(parsed.endDate),
      };
    }
    return getDefaultDateRange();
  });

  const [loading, setLoading] = useState(false);
  const [isDetailedView, setIsDetailedView] = useState(false);

  // Persist activeTab to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ACTIVE_TAB, activeTab);
  }, [activeTab]);

  // Persist dateRange to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.DATE_RANGE, JSON.stringify(dateRange));
  }, [dateRange]);

  const handleDateChange = (newRange) => {
    setDateRange(newRange);
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setIsDetailedView(false);
  };

  const handleExport = () => {
    console.log("Exporting report...");
  };

  // Render active tab content
  const renderContent = () => {
    switch (activeTab) {
      case "source":
        return <Source dateRange={dateRange} onDateChange={handleDateChange} />;
      case "services":
        return (
          <Services dateRange={dateRange} onDateChange={handleDateChange} />
        );
      case "booking":
        return (
          <Booking dateRange={dateRange} onDateChange={handleDateChange} />
        );
      case "conversion":
        return (
          <Conversion
            dateRange={dateRange}
            onDateChange={handleDateChange}
            onDetailedViewChange={setIsDetailedView}
          />
        );
      case "detailed":
        return (
          <Detailed dateRange={dateRange} onDateChange={handleDateChange} />
        );
      case "employees":
        return (
          <Employees
            dateRange={dateRange}
            onDetailedViewChange={setIsDetailedView}
          />
        );
      case "payments":
        return (
          <Payments dateRange={dateRange} onDateChange={handleDateChange} />
        );
      default:
        return <Source dateRange={dateRange} onDateChange={handleDateChange} />;
    }
  };

  if (loading) {
    return <ReportLoader />;
  }

  return (
    <Box
      sx={{
        height: "100vh",
        bgcolor: colors.bgPrimary,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "#F8FAFC",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "#E2E8F0",
            borderRadius: "4px",
            "&:hover": {
              bgcolor: "#CBD5E1",
            },
          },
        }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
};

export default Reports_v2;
